import { defu } from 'defu'
import type { TieredMenuItem } from '~/components/App/Menu/AppMenuTiered.vue'
import { ColorOption } from '~/types/Colors'

export const colorOptionHexes: Record<ColorOption, string> = {
  [ColorOption.Pink]: '#db2777',
  [ColorOption.Red]: '#dc2626',
  [ColorOption.Orange]: '#ea580c',
  [ColorOption.Yellow]: '#ca8a04',
  [ColorOption.Green]: '#16a34a',
  [ColorOption.Teal]: '#0d9488',
  [ColorOption.Sky]: '#0284c7',
  [ColorOption.Indigo]: '#4f46e5',
  [ColorOption.Purple]: '#9333ea',
  [ColorOption.Primary]: '#4f46e5',
  [ColorOption.Error]: '#D92D20',
  [ColorOption.Success]: '#039855',
  [ColorOption.Grey]: '#374151',
}

export const colorOptionClasses: Record<ColorOption, string> = {
  [ColorOption.Pink]: 'text-pink-600',
  [ColorOption.Red]: 'text-red-600',
  [ColorOption.Orange]: 'text-orange-600',
  [ColorOption.Yellow]: 'text-yellow-600',
  [ColorOption.Green]: 'text-green-600',
  [ColorOption.Teal]: 'text-teal-600',
  [ColorOption.Sky]: 'text-sky-600',
  [ColorOption.Indigo]: 'text-indigo-600',
  [ColorOption.Purple]: 'text-purple-600',
  [ColorOption.Primary]: 'text-indigo-600',
  [ColorOption.Error]: 'text-red-600',
  [ColorOption.Success]: 'text-green-600',
  [ColorOption.Grey]: 'text-gray-700',
}

export const colorOptionLabels: Record<ColorOption, string> = {
  [ColorOption.Pink]: 'Pink',
  [ColorOption.Red]: 'Red',
  [ColorOption.Orange]: 'Orange',
  [ColorOption.Yellow]: 'Yellow',
  [ColorOption.Green]: 'Green',
  [ColorOption.Teal]: 'Teal',
  [ColorOption.Sky]: 'Sky',
  [ColorOption.Indigo]: 'Indigo',
  [ColorOption.Purple]: 'Purple',
  [ColorOption.Primary]: 'Primary',
  [ColorOption.Error]: 'Error',
  [ColorOption.Success]: 'Success',
  [ColorOption.Grey]: 'Grey',
}

export const colorOptionLabelsWithoutSemantic: Partial<
  Record<ColorOption, string>
> = {
  [ColorOption.Pink]: 'Pink',
  [ColorOption.Red]: 'Red',
  [ColorOption.Orange]: 'Orange',
  [ColorOption.Yellow]: 'Yellow',
  [ColorOption.Green]: 'Green',
  [ColorOption.Teal]: 'Teal',
  [ColorOption.Sky]: 'Sky',
  [ColorOption.Indigo]: 'Indigo',
  [ColorOption.Purple]: 'Purple',
  [ColorOption.Grey]: 'Grey',
}

export function getHexColor(color: ColorOption) {
  return colorOptionHexes[color]
}

export function getColorOptionClass(color: ColorOption) {
  return colorOptionClasses[color]
}

export function getRandomColorOption(options?: {
  omitPrimary: boolean
  omitSemantic: boolean
}): ColorOption {
  const _options = defu(options, {
    omitPrimary: false,
    omitSemantic: false,
  })

  const colorOptions = Object.values(ColorOption).filter((color) => {
    if (_options.omitPrimary && color === ColorOption.Primary) {
      return false
    }

    if (_options.omitSemantic) {
      if (color === ColorOption.Success || color === ColorOption.Error) {
        return false
      }
    }

    return true
  })

  return colorOptions[Math.floor(Math.random() * colorOptions.length)]
}

export function getColorOptionMenuItems(): TieredMenuItem[] {
  return Object.values(ColorOption).map((color) => ({
    label: colorOptionLabels[color],
    value: color,
    color,
  }))
}

export function getColorOptionMenuItemsWithoutSemantic(): TieredMenuItem[] {
  return Object.keys(colorOptionLabelsWithoutSemantic)
    .map((color) => ({
      label: colorOptionLabelsWithoutSemantic[color as ColorOption],
      value: color,
      color: color as ColorOption,
    }))
    .filter((c) => !!c.label)
}
